import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { UnpaidOrderService } from 'app/api/unpaid-order.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import moment from 'moment';

@Component({
  selector: 'meu-app-my-debt',
  templateUrl: './app-my-debt.component.html',
  styleUrls: ['./app-my-debt.component.scss']
})
export class AppMyDebtComponent implements OnInit, OnDestroy {
  unpaidOrders: any[] = [];
  totalOrders = 0;
  pageIndex = 1;
  pageSize = 10;
  isLoading = false;

  // Date filter (Mặc định: 30 ngày gần nhất)
  customStartDate: any = moment().subtract(30, 'days').toDate();
  customEndDate: any = new Date();
  selectedDateType: string = 'last_30_days';
  dateTypeOptions = [
    { label: 'Tất cả', value: 'all' },
    { label: 'Hôm nay', value: 'today' },
    { label: '7 ngày gần nhất', value: 'last_7_days' },
    { label: '30 ngày gần nhất', value: 'last_30_days' },
    { label: 'Tuần này', value: 'this_week' },
    { label: 'Tháng này', value: 'this_month' },
    { label: 'Quý này', value: 'this_quarter' },
    { label: 'Năm nay', value: 'this_year' },
    { label: 'Tùy chọn', value: 'custom' },
  ];

  // Search filter
  searchText: string = '';
  searchSubject = new Subject<string>();
  private searchSubscription: Subscription;

  // Payment history modal state
  selectedOrder: any = null;
  paymentHistoryList: any[] = [];
  historyPageIndex = 1;
  historyPageSize = 10;
  totalHistory = 0;
  isLoadingHistory = false;

  // Custom debt config modal state
  selectedDebtConfig: any = null;
  isLoadingDebtConfig = false;
  debtConfigLoadError = false;

  constructor(
    private unpaidOrderService: UnpaidOrderService,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(searchValue => {
      this.searchText = searchValue;
      this.onSearch();
    });

    this.loadOrders();
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  /**
   * Tính startDate và endDate từ kiểu thời gian hoặc customStartDate/customEndDate tuỳ chọn.
   */
  getDateRange(): { startDate?: string; endDate?: string } {
    if (!this.selectedDateType || this.selectedDateType === 'all') {
      return {};
    }
    switch (this.selectedDateType) {
      case 'today': {
        const today = moment().format('YYYY-MM-DD');
        return { startDate: today, endDate: today };
      }
      case 'last_7_days':
        return {
          startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD')
        };
      case 'last_30_days':
        return {
          startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD')
        };
      case 'this_week':
        return {
          startDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
          endDate: moment().endOf('isoWeek').format('YYYY-MM-DD')
        };
      case 'this_month':
        return {
          startDate: moment().startOf('month').format('YYYY-MM-DD'),
          endDate: moment().endOf('month').format('YYYY-MM-DD')
        };
      case 'this_quarter':
        return {
          startDate: moment().startOf('quarter').format('YYYY-MM-DD'),
          endDate: moment().endOf('quarter').format('YYYY-MM-DD')
        };
      case 'this_year':
        return {
          startDate: moment().startOf('year').format('YYYY-MM-DD'),
          endDate: moment().endOf('year').format('YYYY-MM-DD')
        };
      case 'custom': {
        const startDate = this.customStartDate ? moment(this.customStartDate).format('YYYY-MM-DD') : undefined;
        const endDate = this.customEndDate ? moment(this.customEndDate).format('YYYY-MM-DD') : undefined;
        return { startDate, endDate };
      }
      default:
        return {};
    }
  }

  onDateTypeChange() {
    this.pageIndex = 1;
    this.loadOrders();
  }

  onCustomDateChange() {
    this.pageIndex = 1;
    this.loadOrders();
  }

  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  onSearch() {
    this.pageIndex = 1;
    this.loadOrders();
  }

  loadOrders() {
    this.isLoading = true;
    const { startDate, endDate } = this.getDateRange();

    this.unpaidOrderService.getUnpaidUserOrders(
      this.pageIndex,
      this.pageSize,
      startDate,
      endDate,
      this.searchText
    ).subscribe(res => {
      this.isLoading = false;
      if (res && res.data) {
        this.unpaidOrders = res.data.collection || [];
        this.totalOrders = res.data.total || 0;
      } else {
        this.unpaidOrders = [];
        this.totalOrders = 0;
      }
      this.cdr.detectChanges();
    }, () => {
      this.isLoading = false;
      this.unpaidOrders = [];
      this.totalOrders = 0;
      this.cdr.detectChanges();
    });
  }

  changePage(page: number) {
    this.pageIndex = page;
    this.loadOrders();
  }

  paginatorChange(event: any) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex + 1;
    this.loadOrders();
  }

  getTotalPaid(order: any): number {
    if (!order || !order.extend_order_payments) return 0;
    return order.extend_order_payments.reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
  }

  openHistoryModal(order: any, modalTemplate: any) {
    this.selectedOrder = order;
    this.paymentHistoryList = [];
    this.historyPageIndex = 1;
    this.totalHistory = 0;
    this.loadPaymentHistory();
    this.modalService.open(modalTemplate, {
      size: 'lg',
      scrollable: true,
      centered: true
    });
  }

  loadPaymentHistory() {
    if (!this.selectedOrder) return;
    this.isLoadingHistory = true;
    this.unpaidOrderService.getOrderPaymentsByOrder(
      this.selectedOrder.id,
      this.historyPageIndex,
      this.historyPageSize
    ).subscribe(res => {
      this.isLoadingHistory = false;
      if (res && res.success && res.data) {
        this.paymentHistoryList = res.data.collection || [];
        this.totalHistory = res.data.total || 0;
      }
      this.cdr.detectChanges();
    }, () => {
      this.isLoadingHistory = false;
      this.cdr.detectChanges();
    });
  }

  changeHistoryPage(page: number) {
    this.historyPageIndex = page;
    this.loadPaymentHistory();
  }

  openDebtConfigModal(order: any, modalTemplate: any) {
    this.selectedOrder = order;
    this.selectedDebtConfig = null;
    this.debtConfigLoadError = false;
    this.isLoadingDebtConfig = true;

    this.modalService.open(modalTemplate, {
      size: 'md',
      centered: true
    });

    this.unpaidOrderService.getOrderDebtConfig(order.id).subscribe(res => {
      this.isLoadingDebtConfig = false;
      this.selectedDebtConfig = (res && res.success && res.data) ? res.data : null;
      this.cdr.detectChanges();
    }, () => {
      this.isLoadingDebtConfig = false;
      this.selectedDebtConfig = null;
      this.debtConfigLoadError = true;
      this.cdr.detectChanges();
    });
  }
}

